import React, { useMemo } from 'react';
import {
    DetailDossierTitle,
    DetailDossierToolbar,
    useCurrentDossierCtx,
} from '@europrocurement/l2d-modules';

import { prescripteursBasePath } from '../../../Prescripteurs/constants/paths';

/**
 * Composant header du détail d'un dossier.
 * Gère le chargement progressif des données (prescripteur, offre, client opéré)
 * et affiche un skeleton durant le chargement.
 */
const DetailDossierHeader: React.FunctionComponent = function () {
    const { dossier, siblings } = useCurrentDossierCtx();

    /** Données du dossier formatées pour l'affichage */
    const formattedDossier = useMemo(
        () => ({
            statut: dossier?.statutLibelle as string,
            societeRef: dossier?.societeRef as string,
            statutId: dossier?.statutId as number,
            id: dossier?.id as number,
        }),
        [dossier?.statutLibelle, dossier?.societeRef, dossier?.statutId, dossier?.id],
    );

    /** Données du prescripteur formatées pour l'affichage */
    const formattedPrescripteur = useMemo(
        () => ({
            id: dossier?.prescripteurId as number,
            raisonSociale: (dossier?.prescripteurLibelle as string) || 'Pas de raison sociale',
            codeComptable: dossier?.prescripteurCodeCompta || 'Pas de code comptable',
            /** URL vers la page de détail du prescripteur */
            urlToDetails: `/${prescripteursBasePath}/liste/${dossier?.prescripteurId}`,
        }),
        [dossier?.prescripteurId, dossier?.prescripteurLibelle, dossier?.prescripteurCodeCompta],
    );
    /** Données de l'offre formatées pour l'affichage */
    const formattedOffre = useMemo(
        () => ({
            libelle: dossier?.offreLibelle || 'Aucune offre',
        }),
        [dossier?.offreLibelle],
    );

    /** Données du client opéré formatées pour l'affichage */
    const formattedClientOpere = useMemo(
        () => ({
            raisonSociale: dossier?.clientOpereLibelle || 'Pas de raison sociale',
            siren: dossier?.clientOpereSiren as string,
            // URLs et données formatées
            urlToSocieteDotCom: `https://www.societe.com/cgi-bin/fiche?rncs=${dossier?.clientOpereSiren}`,
            id: dossier?.clientOpereId as number,
        }),
        [dossier?.clientOpereLibelle, dossier?.clientOpereSiren, dossier?.clientOpereId],
    );

    const isMultiDossiers = siblings && siblings.length > 1;

    if (!dossier) {
        return null;
    }

    return (
        <>
            {/* Toolbar affichée dès que dossier et siblings sont disponibles */}
            <DetailDossierToolbar
                dossier={dossier}
                source="BACK"
                siblingDossiers={siblings}
                isMultiDossiers={isMultiDossiers}
            />

            {/* Title affiché seulement quand toutes les données sont prêtes */}
            <DetailDossierTitle
                dossier={formattedDossier}
                prescripteur={formattedPrescripteur}
                offre={formattedOffre}
                clientOpere={formattedClientOpere}
                color="primary"
                icon={isMultiDossiers ? 'folders' : 'folder'}
            />
        </>
    );
};

export default DetailDossierHeader;
